// tslint:disable:max-file-line-count
import * as React from 'react';

import { Button, ButtonTheme } from '@cian/button-component';
import { OwnerBadge, TooltipDirection } from '@cian/owner-badge-component';
import { plural } from '@cian/utils/lib/shared/plural';

import { HonestWork, PopupDirection } from '@cian/honestwork-component';
import { Stars } from '@cian/stars-component';

import {
  IAgentAvailabilitySchema,
  IAgentReviewsSchema,
  IAgentSchema,
  IKPSchema,
  INewObjectBuilderSchema,
  IOfferSchema,
  IUserSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { Avatar } from '../../common/components/avatar';
import { Link } from '../../common/components/link';
import { LinkWrapper } from '../../common/components/link_wrapper';
import { Tag } from '../../common/components/tag';
import { AccountType } from '../../store/offer';
import { IOfferChatsState } from '../../store/offer_chats';
import {
  getAgentLabel,
  getAgentName,
  getAgentType,
  getAuthorLabel,
  getLifeTime,
  getProfileLink,
  getTrackingLabelPrefix,
  getVerifyWay,
  isFromBuilder,
  isHomeowner,
  isHomeownerVerified,
  isHonest,
  isOfficialSeller,
  isPro,
  mapSkillsArrayIntoTree,
} from '../../utils/agent';
import { getFormattedPhoneNumber, getNewbuildingsListQueryStringUrl } from '../../utils/formatters';
import { SimpleBotDetector } from '../../utils/simpleBotDetector';
import { getAgentExperience } from '../../utils/getAgentExperience';
import * as AnalyticsAside from '../contacts_aside/tracking';
import { PopupOwnOfferContainer } from '../popups/own_offer/container';
import { PopupStartChatContainer } from '../popups/start_chat/container';
import { ReviewsDividerIcon } from './icons/reviews-divider';
import { isDeveloperCanCallback } from '../../utils/isDeveloperCanCallback';
import * as Analytics from './tracking';
import { ABCallButton } from '../ab_call_button';
import { PartnerTooltip } from '../PartnerTooltip';
import * as styles from './index.css';

export interface IAuthorProps {
  abGroup: number;
  agent: IAgentSchema | null;
  agentReviews?: IAgentReviewsSchema | null;
  developer: INewObjectBuilderSchema | null;
  offer: IOfferSchema;
  offerChat: IOfferChatsState;
  user: IUserSchema | null;
  agentAvailability: IAgentAvailabilitySchema | null;
  kp: IKPSchema | null;
  isAgentExperienceExperimentActive: boolean;
  isDealRequestSubstitutionPhone: boolean;
  showCountrysideBuilderBadge: boolean;

  chatMessageSent(): void;
  openCallbackPopup(): void;
  setPhoneCollapse(): void;
}

export interface IAuthorState {
  isPhonesVisible: boolean;
  isStartChatPopupOpen: boolean;
  isOwnOfferPopupOpen: boolean;
  startChatMessage: string;
}

/* istanbul ignore next */
export class Author extends React.PureComponent<IAuthorProps, IAuthorState> {
  public state = {
    isOwnOfferPopupOpen: false,
    isPhonesVisible: false,
    isStartChatPopupOpen: false,
    startChatMessage: '',
  };
  private botDetector = new SimpleBotDetector();

  public componentDidMount() {
    this.botDetector.init();
  }

  public render() {
    const {
      agent,
      agentReviews,
      developer,
      offer,
      user,
      kp,
      openCallbackPopup,
      chatMessageSent,
      isAgentExperienceExperimentActive,
      isDealRequestSubstitutionPhone,
      showCountrysideBuilderBadge,
    } = this.props;
    const { phones } = offer;
    const { isStartChatPopupOpen, startChatMessage, isOwnOfferPopupOpen } = this.state;

    if (!agent) {
      return null;
    }

    const { isCianPartner, isDeveloper, isSubAgent, accountType, status: agentStatus } = agent;

    /**
     * Указывает на сабагента с опубликованным мастер-аккаунтом
     *
     * В таком случае бекенд присылает урл аватара,
     * поэтому вычисляем по косвенному признаку - наличию аватара у субагента
     */
    const isSubagentWithPublishedMaster = isSubAgent && agent.absoluteAvatarUrl;
    const isSpecialist = accountType === 'specialist';
    const isPublishedAgent = agentStatus === 'published';
    const isProAgent = isPro(agent);
    const isHonestAgent = isHonest(agent);
    const isAgentAndPublishedProfile = !isSubAgent && isPublishedAgent;

    const profileLink = getProfileLink(agent, offer, developer);

    const hasReviews = agentReviews && agentReviews.totalCount > 0;
    const hasRate = agentReviews && agentReviews.entityRate && agentReviews.entityRate > 0;
    const rate = (agentReviews && agentReviews.entityRate) || 0;
    const reviewsCount = (agentReviews && agentReviews.totalCount) || 0;

    const name = getAgentName(agent, offer);
    const label = getAuthorLabel(offer, showCountrysideBuilderBadge) || getAgentLabel(agent.accountType, offer);
    const isVerified = isHomeownerVerified(offer, agent);
    const verifyWay = getVerifyWay(offer, agent);

    // Если обяъвление загородки привязано к коттеджному поселку,
    // проверяем возможность отображения телефонов у застройщика.
    const shouldRenderContactButtons = kp ? kp.displayBuilderPhone : true;

    return (
      <section className={styles['container']} id="author">
        {(isPublishedAgent || isSubagentWithPublishedMaster) && (
          <LinkWrapper
            href={profileLink}
            target="_blank"
            onClick={() => Analytics.trackGoToCard(`${getTrackingLabelPrefix(offer)}ava`)}
            className={styles['link-wrapper']}
          >
            <Avatar
              className={styles['image']}
              isRoundness={
                agent.accountType !== AccountType.Agency &&
                !isDeveloper &&
                !isFromBuilder(offer) &&
                !isOfficialSeller(offer)
              }
              url={agent.absoluteAvatarUrl || ''}
              character={(!isHomeowner(offer) && name && name[0]) || ''}
            />
          </LinkWrapper>
        )}
        {((isAgentAndPublishedProfile && agent.accountType) || (agent.isSubAgent && agent.accountType)) && (
          <div className={styles['type']}>{getAgentType(agent, offer)}</div>
        )}
        <div className={styles['header']}>
          <LinkWrapper
            className={styles['title-link']}
            href={profileLink}
            target="_blank"
            onClick={() => Analytics.trackGoToCard(`${getTrackingLabelPrefix(offer)}name`)}
          >
            <h2 className={styles['title']}>{name || `ID ${agent.id}`}</h2>
          </LinkWrapper>
          {isHonestAgent && (
            <HonestWork
              agentLink={{
                agentId: agent.id,
                otherLinkProps: {
                  deal_type: offer.dealType,
                  offer_type: offer.offerType,
                },
              }}
              direction={PopupDirection.RIGHT}
              className={styles['honest']}
            />
          )}
          {isProAgent && (
            <Tag className={styles['tag']} title="Профессиональный пользователь">
              Pro
            </Tag>
          )}
          {isCianPartner && (
            <div className={styles['tag']}>
              <PartnerTooltip master={<Tag className={styles['tag-partner']}>Партнёр</Tag>} />
            </div>
          )}
          {label === 'homeowner' ? (
            <div className={styles['tag']}>
              <OwnerBadge isVerified={isVerified} direction={TooltipDirection.RIGHT} verifyWay={verifyWay} />
            </div>
          ) : (
            <Tag className={styles['tag']}>{label}</Tag>
          )}
        </div>
        {isSpecialist && isPublishedAgent && (
          <div className={styles['reviews']}>
            {hasRate && (
              <div className={styles['rate']}>
                {(rate * 5)
                  .toFixed(1)
                  .toString()
                  .replace('.', ',')}
              </div>
            )}
            {hasRate && (
              <div className={styles['stars']}>
                <Stars value={rate} percentage uniqueKey={`${agent.id}`} unitClassName={styles['reviews-star']} />
              </div>
            )}
            {(!hasReviews || (hasReviews && !hasRate)) && <div className={styles['no-reviews']}>Нет отзывов</div>}
            {hasReviews && hasRate && (
              <div className={styles['reviews-divider']}>
                <ReviewsDividerIcon />
              </div>
            )}
            {hasReviews && hasRate && (
              <div className={styles['reviews-count']}>
                <Link className={styles['reviews-count-link']} href={`/agents/${agent.id}`}>
                  {reviewsCount} {plural(reviewsCount, ['отзыв', 'отзыва', 'отзывов'])}
                </Link>
              </div>
            )}
          </div>
        )}
        <div className={styles['subtitles']}>
          {agent.experience && isSpecialist && isAgentExperienceExperimentActive && (
            <span className={styles['experience']}>{getAgentExperience(agent)}</span>
          )}
          {(getLifeTime(agent) ||
            (!isDeveloper && Boolean(agent.offersCount) && agent.offersLink) ||
            (isDeveloper && developer && developer.stats)) && (
            <div className={styles['subtitle']}>
              <span className={styles['lifetime']}>{getLifeTime(agent)} на ЦИАН</span>
              {!isDeveloper && Boolean(agent.offersCount) && agent.offersLink && (
                <Link
                  className={styles['offers-link']}
                  href={agent.offersLink}
                  target="_blank"
                  onClick={() => Analytics.trackGoToCard(`${getTrackingLabelPrefix(offer)}qty`)}
                >
                  {agent.offersCount} {plural(Number(agent.offersCount), ['объявление', 'объявления', 'объявлений'])}
                </Link>
              )}
              {isFromBuilder(offer) && agent.isDeveloper && developer && developer.stats && (
                <ul className={styles['company']}>
                  {developer.stats.done && developer.stats.done.qs && developer.stats.done.text ? (
                    <li className={styles['company-item']}>
                      <span>Сдано</span>
                      <Link
                        className={styles['company-link']}
                        href={getNewbuildingsListQueryStringUrl(developer.stats.done.qs)}
                        onClick={() => Analytics.trackClickBuilder('to_developerCard_ready_JK')}
                        target="_blank"
                      >
                        {developer.stats.done.text}
                      </Link>
                    </li>
                  ) : (
                    <li className={styles['company-item']}>
                      <span>Нет сданных ЖК</span>
                    </li>
                  )}
                  {developer.stats.progress && developer.stats.progress.qs && developer.stats.progress.text ? (
                    <li className={styles['company-item']}>
                      <span>Строится</span>
                      <Link
                        className={styles['company-link']}
                        href={getNewbuildingsListQueryStringUrl(developer.stats.progress.qs)}
                        onClick={() => Analytics.trackClickBuilder('to_developerCard_in_progress_JK')}
                        target="_blank"
                      >
                        {developer.stats.progress.text}
                      </Link>
                    </li>
                  ) : (
                    <li className={styles['company-item']}>
                      <span>Нет строящихся ЖК</span>
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
        {this.renderDescription()}
        {isFromBuilder(offer) && profileLink && (
          <Link
            className={styles['developer-link']}
            href={profileLink}
            target="_blank"
            rel="nofollow noopener"
            onClick={() => Analytics.trackClickBuilder('to_developerCard_more')}
          >
            Подробнее о застройщике
          </Link>
        )}
        {phones && !phones.some(phone => !phone.countryCode || !phone.number) && shouldRenderContactButtons && (
          <div className={styles['buttons']}>
            {this.state.isPhonesVisible ? this.showPhones() : this.renderShowPhoneButton()}
            {isDeveloperCanCallback(agent, isDealRequestSubstitutionPhone) && (
              <Button
                theme={ButtonTheme.Ghost}
                onClick={() => {
                  Analytics.trackClickCallbackMe(offer, agent);
                  openCallbackPopup();
                }}
              >
                Перезвоните мне
              </Button>
            )}
            {this.renderSendMessageButton()}
          </div>
        )}
        {this.state.isPhonesVisible && (
          <div className={styles['wish']}>Пожалуйста, скажите, что нашли это объявление на ЦИАН</div>
        )}
        <PopupStartChatContainer
          isOpen={isStartChatPopupOpen}
          message={startChatMessage}
          onMessageChange={message => this.setState({ startChatMessage: message })}
          onMessageSent={() => {
            chatMessageSent();

            Analytics.trackFirstMessageSent(offer, user);
          }}
          onClose={() => this.setState({ isStartChatPopupOpen: false })}
        />
        <PopupOwnOfferContainer
          isOpen={isOwnOfferPopupOpen}
          onClose={() => this.setState({ isOwnOfferPopupOpen: false })}
        />
      </section>
    );
  }

  private renderShowPhoneButton = () => {
    const { abGroup, offer, agent, setPhoneCollapse, user, kp } = this.props;

    return offer.status === 'published' ? (
      <Button
        onClick={() => {
          Analytics.trackClickShowPhone(offer, agent, abGroup, user, kp && kp.id, !this.botDetector.isHuman());

          setPhoneCollapse();
          this.handleClickOnShowPhonesButton();

          this.openChatFromPhones();
        }}
        className={styles['button']}
      >
        <ABCallButton />
      </Button>
    ) : null;
  };

  private renderSendMessageButton = () => {
    const { user, offer } = this.props;
    const { isChatsEnabled, chatId, isChatMessageSent } = this.props.offerChat;
    const isOfferOwnedByUser = user && user.isAuthenticated && user.realtyUserId === offer.userId;

    return isChatsEnabled ? (
      <Button
        theme={ButtonTheme.Ghost}
        onClick={() => {
          this.onStartChatButtonClick();
        }}
      >
        {(!!chatId || isChatMessageSent) && !isOfferOwnedByUser ? 'Посмотреть сообщения' : 'Написать сообщение'}
      </Button>
    ) : null;
  };

  private onStartChatButtonClick = () => {
    const { agentAvailability, offer, user, agent } = this.props;
    const { chatId, isChatMessageSent } = this.props.offerChat;

    const isAgentNotAvailable = agentAvailability && !agentAvailability.available;
    const isAuthenticated = user && user.isAuthenticated;
    const isOfferOwnedByUser = isAuthenticated && user && user.realtyUserId === offer.userId;
    Analytics.trackFirstMessageClick(offer, agent); // <- клик на кнопку!! В любом случае должен отправляться!!
    switch (true) {
      case !isAuthenticated:
        //tslint:disable-next-line:no-any
        if ((window as any).waitForLoginPopup) {
          (window as any).waitForLoginPopup('chats'); //tslint:disable-line:no-any
        }
        break;
      case Boolean(isChatMessageSent || !!chatId):
        Analytics.trackGoToMessages();
        window.location.href = `/dialogs/?offerId=${offer.id}`;
        break;
      case isOfferOwnedByUser:
        this.setState({ isOwnOfferPopupOpen: true });
        break;
      default:
        this.setState({ isStartChatPopupOpen: true });
        AnalyticsAside.trackFirstMessageShowPopup(offer, agent, isAgentNotAvailable ? 'open_message' : undefined);
    }
  };

  private openChatFromPhones = () => {
    const { offer, user, agent, agentAvailability } = this.props;
    const { isChatsEnabled } = this.props.offerChat;

    const isAuthenticated = user && user.isAuthenticated;
    const isNotAvailable = Boolean(agentAvailability && !agentAvailability.available);
    const isOfferOwnedByUser = isAuthenticated && user && user.realtyUserId === offer.userId;

    if (isChatsEnabled && isNotAvailable) {
      if (isOfferOwnedByUser) {
        return this.setState({ isOwnOfferPopupOpen: true });
      }

      if (isAuthenticated) {
        this.setState({ isStartChatPopupOpen: true });
        AnalyticsAside.trackFirstMessageShowPopup(offer, agent, 'open_phone');
      } else {
        //tslint:disable-next-line:no-any
        if ((window as any).waitForLoginPopup) {
          (window as any).waitForLoginPopup(); //tslint:disable-line:no-any
        }
      }
    }
  };

  private handleClickOnShowPhonesButton = () => {
    this.setState({
      isPhonesVisible: true,
    });
  };

  private showPhones = () => {
    const { phones } = this.props.offer;

    if (!phones || phones.some(phone => !phone.countryCode || !phone.number)) {
      return null;
    }

    return <span className={styles['phones']}>{phones.map(getFormattedPhoneNumber).join(', ')}</span>;
  };

  private renderDescription = () => {
    const { agent, offer } = this.props;
    let description: string | null = null;

    if (!agent) {
      return null;
    }

    if (isFromBuilder(offer)) {
      description = agent.description;
    } else {
      description = mapSkillsArrayIntoTree(agent.skills)
        .map(({ childs, name }) => `${name}: ${childs.map(child => child.name).join(', ')}`)
        .join('. ');
      if (description) {
        description = `${description}.`;
      }
    }

    if (!description) {
      return null;
    }

    if (description.length > 150) {
      description = description.substr(0, 150).concat('...');
    }

    return <div className={styles['description']}>{description}</div>;
  };
}
